import PageLayout from "@/components/PageLayout";
import OutilInterneSection from "@/components/sections/OutilInterneSection";

const PILLARS = [
  {
    title: "Cartographier les sources",
    desc: "Identifier les registres officiels, les bases open data, les agrégateurs privés, les fiches dirigeants, les moteurs de recherche et les rediffusions secondaires.",
  },
  {
    title: "Comparer les données",
    desc: "Mettre en regard les données issues des sources officielles avec leurs reprises visibles en ligne.",
  },
  {
    title: "Qualifier les reprises",
    desc: "Distinguer les sources publiques, les rediffusions privées, les indexations automatiques et les situations nécessitant une qualification plus précise.",
  },
  {
    title: "Suivre les démarches",
    desc: "Centraliser les demandes, les réponses, les relances et les éléments de preuve.",
  },
  {
    title: "Prioriser les actions",
    desc: "Repérer les démarches les plus pertinentes selon la visibilité, la nature des données et les possibilités réelles d'intervention.",
  },
];

function SectionEyebrow({ n, label }) {
  return (
    <p className="label-eyebrow text-[#666A70] mb-6">
      {String(n).padStart(2, "0")} / {label}
    </p>
  );
}

export default function OutilInterne() {
  return (
    <PageLayout
      eyebrow="Outil interne"
      title="Outil interne"
      intro="Un support de cartographie et de suivi pour documenter l'exposition numérique. L'outil ne décide pas à la place de la méthode : il structure l'observation, conserve les preuves et permet une priorisation rigoureuse au cas par cas."
    >
      {/* Section 01 — 5 piliers méthodologiques */}
      <section
        data-testid="outil-section-pillars"
        className="bg-[#FAF9F7] border-b border-[#E2DEDA]"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <SectionEyebrow n={1} label="Périmètre" />
              <h2 className="font-serif font-normal text-[#181A1D] text-3xl lg:text-4xl xl:text-5xl tracking-tight leading-[1.06] text-balance">
                Cinq fonctions complémentaires.
              </h2>
            </div>
            <div className="lg:col-span-7 flex items-end">
              <p className="text-[15px] lg:text-base text-[#52565C] font-normal leading-relaxed max-w-2xl">
                L'outil interne ne se substitue pas à une analyse au cas par
                cas. Il en facilite la rigueur en regroupant cinq fonctions
                qui structurent chaque diagnostic.
              </p>
            </div>
          </div>

          <ul
            data-testid="outil-pillars"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 lg:gap-4"
          >
            {PILLARS.map((p, i) => (
              <li
                key={p.title}
                data-testid={`outil-pillar-${i}`}
                className="bg-white rounded-md border border-[#E2DEDA] p-7 lg:p-8 flex flex-col"
              >
                <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#8B8F95] mb-5">
                  {String(i + 1).padStart(2, "0")} / 05
                </span>
                <h3 className="font-serif text-lg lg:text-xl text-[#181A1D] font-normal leading-snug tracking-tight mb-4 text-balance">
                  {p.title}
                </h3>
                <div className="h-[2px] w-8 bg-[#E94B3C] mb-4" />
                <p className="text-[13px] lg:text-sm text-[#5A5E64] font-normal leading-relaxed text-pretty">
                  {p.desc}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Section 02 — Mockup d'interface (sober, no abstract image) */}
      <OutilInterneSection />

      {/* Section 03 — Posture : support de méthode, pas automate */}
      <section
        data-testid="outil-section-posture"
        className="bg-[#F2F0ED] border-t border-[#E2DEDA]"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionEyebrow n={3} label="Positionnement" />
            <h2 className="font-serif font-normal text-[#181A1D] text-3xl lg:text-4xl xl:text-5xl tracking-tight leading-[1.06] text-balance">
              Un support de méthode, pas une automatisation.
            </h2>
            <div className="h-[2px] w-10 bg-[#E94B3C] mt-10" />
          </div>
          <div className="lg:col-span-7 space-y-6 text-[15px] lg:text-base text-[#52565C] font-normal leading-relaxed">
            <p>
              L'outil interne n'est pas conçu pour produire des résultats
              automatiques. Chaque situation est qualifiée manuellement, en
              tenant compte de la nature des données, des obligations
              légales applicables et du contexte réel.
            </p>
            <p>
              Ce que l'outil apporte se situe ailleurs : conserver une trace
              fiable des sources observées, faciliter la comparaison entre
              données officielles et rediffusions, organiser les démarches
              dans la durée et éviter les angles morts.
            </p>
            <aside
              data-testid="outil-note"
              className="mt-6 border border-[#E2DEDA] bg-white rounded-md p-7 lg:p-8 max-w-2xl"
            >
              <p className="label-eyebrow text-[#666A70] mb-3">Réserve</p>
              <p className="text-[13px] lg:text-sm text-[#5A5E64] italic font-normal leading-relaxed text-pretty">
                L'outil ne promet ni effacement total des données publiques,
                ni résultat garanti. Il documente, qualifie et priorise : la
                décision et la démarche restent humaines.
              </p>
            </aside>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
